// ===========================================
// Reopen Term Dialog
// ===========================================
// ADMIN-only override of a term lock (plan md/plan/term-lock-at-next-term-start.md §4). A reason
// (5-500 characters) is required; the duration is "until the end of" a SCHOOL day, today … +6
// days (the server sends `days` 1..7). The lock returns by itself, no cron.

import { useEffect, useState } from 'react';
import { Alert, Box, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import FormModal from '../common/FormModal';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { formatUTCDate, schoolTodayUtcMidnight } from '../../utils/dateUtils';
import { schoolDayLabel } from '../../utils/termLock';

export const REOPEN_REASON_MIN = 5;
export const REOPEN_REASON_MAX = 500;

interface ReopenTermDialogProps {
  open: boolean;
  termName: string;
  loading?: boolean;
  /** The server's refusal, shown inside the dialog. */
  error?: string | null;
  onSubmit: (input: { reason: string; days: number }) => void;
  onClose: () => void;
}

const dayWord = (schoolToday: Date, i: number): string =>
  i === 0
    ? 'today'
    : i === 1
      ? 'tomorrow'
      : formatUTCDate(new Date(schoolToday.getTime() + i * 86_400_000), { weekday: 'long' }, 'en-AU');

export default function ReopenTermDialog({
  open,
  termName,
  loading = false,
  error,
  onSubmit,
  onClose,
}: ReopenTermDialogProps) {
  const timeZone = useSchoolTimezone();
  const [reason, setReason] = useState('');
  const [days, setDays] = useState(1);

  useEffect(() => {
    if (open) {
      setReason('');
      setDays(1);
    }
  }, [open]);

  const schoolToday = schoolTodayUtcMidnight(timeZone);
  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= REOPEN_REASON_MIN && trimmed.length <= REOPEN_REASON_MAX;
  const lastDay = schoolDayLabel(schoolToday, days - 1);

  return (
    <FormModal
      open={open}
      title={`Reopen ${termName}`}
      submitLabel="Reopen"
      loading={loading}
      submitDisabled={!reasonValid}
      onSubmit={() => onSubmit({ reason: trimmed, days })}
      onClose={onClose}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant="body2">
          While {termName} is reopened, anyone who can manage classes can change its classes,
          enrolments, clinic bookings and make-ups. Changes to past days are recorded in the
          Retroactive edits report. It locks again automatically at the end of {lastDay}.
        </Typography>
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          multiline
          minRows={2}
          fullWidth
          inputProps={{ maxLength: REOPEN_REASON_MAX }}
          error={reason.length > 0 && !reasonValid}
          helperText={
            reason.length > 0 && trimmed.length < REOPEN_REASON_MIN
              ? `At least ${REOPEN_REASON_MIN} characters. ${reason.length}/${REOPEN_REASON_MAX}`
              : `${reason.length}/${REOPEN_REASON_MAX}`
          }
        />
        <FormControl fullWidth>
          <InputLabel id="reopen-days-label">Reopen until</InputLabel>
          <Select
            labelId="reopen-days-label"
            label="Reopen until"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
              <MenuItem key={n} value={n}>
                {`Until the end of ${dayWord(schoolToday, n - 1)} (${schoolDayLabel(schoolToday, n - 1)})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    </FormModal>
  );
}
